/**
 * useLandlordPlan — tenant-side hook that fetches the landlord's
 * effective plan via the `get_my_landlord_plan` RPC.
 *
 * Used to gate tenant-side features like chat and maintenance based
 * on what the landlord has paid for.
 */

import { useEffect, useState } from 'react';
import { supabase } from './supabase';
import { getEffectivePlan, type Plan } from './plans';
import { useAppContext } from '../context/useAppContext';

interface LandlordPlanState {
    plan: Plan;
    loading: boolean;
    hasLink: boolean;
    stripeConnectChargesEnabled: boolean;
    landlordId: string | null;
}

export function useLandlordPlan(): LandlordPlanState {
    const { user } = useAppContext();
    const [state, setState] = useState<LandlordPlanState>({
        plan: 'Free',
        loading: true,
        hasLink: false,
        stripeConnectChargesEnabled: false,
        landlordId: null,
    });

    useEffect(() => {
        if (!user.id) return;
        let cancelled = false;

        (async () => {
            const { data, error } = await supabase.rpc('get_my_landlord_plan');
            if (cancelled) return;

            if (error) {
                console.error('[useLandlordPlan] RPC error:', error);
                setState({ plan: 'Free', loading: false, hasLink: false, stripeConnectChargesEnabled: false, landlordId: null });
                return;
            }

            const row = Array.isArray(data) ? data[0] : data;
            if (!row) {
                setState({ plan: 'Free', loading: false, hasLink: false, stripeConnectChargesEnabled: false, landlordId: null });
                return;
            }

            const effective = getEffectivePlan(row.plan, row.subscription_status);
            setState({
                plan: effective,
                loading: false,
                hasLink: true,
                stripeConnectChargesEnabled: Boolean(row.stripe_connect_charges_enabled),
                landlordId: row.landlord_id ?? null,
            });
        })();

        return () => { cancelled = true; };
    }, [user.id]);

    return state;
}
